import React, { FC, useCallback, useState } from "react";
import {
  EAcaoLogUser,
  EApplication,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  EUserType,
  IAulasChamada,
  IDiaAula,
  Log,
} from "@deltasge/marauders-map";

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Filter as FilterIcon, PencilPlus as AddIcon } from "mdi-material-ui";

import { api, gravaLogUser, history } from "configs";
import { EmptyState } from "components/EmptyStates";
import { Backdrop } from "components/Backdrop";
import { snack } from "components/GlobalSnackbar";
import { useDialog } from "hooks/useDialog";
import { useFilter } from "hooks/useFilter";
import { IFilterConteudoMinistrado } from "interfaces";
import { Main } from "pages/_layouts/Main";
import { useAppSelector } from "store";
import { getError } from "utils";

import { Filter } from "./components/Filter";
import { useApi } from "hooks/useApi";
import { TableRowAula } from "./components/TableRowAula";
import { EditAula } from "./components/EditAula";
import { Float } from "components/Buttons/Float";
import { format, parseISO } from "date-fns";

export const ConteudoMinistradoAulas: FC = () => {
  const [selecionados, setSelecionados] = useState<IAulasChamada[]>([]);
  const [salvando, setSalvando] = useState(false);

  const {
    configuracoes: {
      configOnline: { configuracoes },
    },
    usuario: { usuario },
    escola,
  } = useAppSelector((state) => state);

  const idProfessor: number | null =
    usuario?.tipoUsuario == EUserType.Professor ? usuario?.id ?? null : null;
  const mostrarTodos = usuario?.tipoUsuario != EUserType.Professor;

  const handleFilterCurso = (idCurso?: number) => {
    setSelecionados([]);
    navigate({
      ...filter,
      idCurso,
      idSerie: undefined,
      idTurma: undefined,
      idMateria: undefined,
    });
  };

  const handleFilterSerie = (idSerie?: number) => {
    setSelecionados([]);
    navigate({
      ...filter,
      idSerie,
      idTurma: undefined,
      idMateria: undefined,
    });
  };

  const handleFilterTurma = (idTurma?: number) => {
    setSelecionados([]);
    navigate({
      ...filter,
      idTurma,
    });
  };

  const handleFilterMateria = (idMateria?: number) => {
    setSelecionados([]);
    navigate({
      ...filter,
      idMateria,
    });
  };

  const handleFilterEtapa = (etapa?: number) => {
    setSelecionados([]);
    navigate({
      ...filter,
      etapa: etapa ?? configuracoes.bim_site,
    });
  };

  const {
    Component,
    navigate,
    filter,
    selecionados: { curso, materia, turma, etapa },
  } = useFilter<IFilterConteudoMinistrado>({
    comRecuperacao: true,
    pathname: "/conteudo-ministrado/",
    queryKeys: [
      "idCurso",
      "idSerie",
      "idTurma",
      "idMateria",
      "etapa",
      "diaSemAula",
      "tarefa",
      "somenteRecuperacao",
    ],
    initialValue: {
      anoLetivo: parseInt(escola.anoSite, 10),
      etapa: configuracoes.bim_site,
    },
    options: {
      curso: {
        onClick: handleFilterCurso,
        mostrarTodos,
      },
      serie: {
        onClick: handleFilterSerie,
        mostrarTodos,
      },
      turma: {
        onClick: handleFilterTurma,
        mostrarTodos,
      },
      materia: {
        onClick: handleFilterMateria,
        mostrarTodos,
      },
      etapa: {
        onClick: handleFilterEtapa,
        mostrarTodos: false,
      },
    },
  });

  const {
    data: diasAulasChamada,
    loading,
    carregar,
  } = useApi<IAulasChamada[]>({
    url:
      filter.idTurma &&
      etapa?.id &&
      (filter.idMateria || usuario?.tipoUsuario === EUserType.UsuarioInterno)
        ? `/educador/conteudoministradoaulas/${filter.idTurma}/periodo/${
            etapa?.id
          }/professor/${idProfessor ? idProfessor : null}/materia/${
            filter.idMateria ? filter.idMateria : null
          }/recuperacao/1`
        : null,
    onError: () => snack.error("Problema ao carregar as aulas"),
    refresh: `${filter.idTurma}_${filter.idMateria}_${
      filter.idEtapa
    }_${idProfessor?.toString()}`,
  });

  const {
    RenderDialog: DialogConteudo,
    show: showDialogConteudo,
    hide: hideDialogConteudo,
  } = useDialog({
    title: "Editar conteúdo",
    options: {
      maxWidth: "sm",
    },
    customClose: (hide) => {
      hide();
      setSelecionados([]);
    },
  });

  const handleGerarAula = useCallback(async (aula: IAulasChamada) => {
    try {
      let idDiaAulas = aula.idDiaAulas;
      if (!aula.idDiaAulas) {
        setSalvando(true);
        const { data } = await api.post<IDiaAula | undefined | null>(
          "diaaula",
          {
            anoLetivo: aula.anoLetivo,
            data: format(parseISO(aula.data), "yyyy-MM-dd HH:mm:ss"),
            aula: aula.aula,
            idCurso: aula.idCurso,
            idSerie: aula.idSerie,
            idTurma: aula.idTurma,
            idMateria: aula.idMateria,
            diaSemAula: false,
            inutilizaDia: false,
            idProfessor:
              usuario?.tipoUsuario === EUserType.Professor ? usuario?.id : null,
            idUsuario:
              usuario?.tipoUsuario === EUserType.UsuarioSistema
                ? usuario?.id
                : null,
            descricao: "",
            idMensagem: null,
            tarefa: "",
            id: null,
            naGrade: true,
            hora: aula.horaIni,
            duracao: aula.duracao,
            origem: EApplication.Educador,
          }
        );

        if (!data) {
          throw new Error("Erro ao gerar o dia de aula");
        }
        await gravaLogUser({
          logKey: {
            ...Log.Alunos.Incluir,
            acao: EAcaoLogUser.Inclusao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: Number(data.idProfessor),
            idMateria: Number(data.idMateria),
            idCurso: Number(data.idCurso),
            idSerie: Number(data.idSerie),
            idTurma: Number(data.idTurma),
          },
          status: ELogUserStatus.Aguardando,
          message: `incluiu aula id: ${data.id}`,
          dataAfter: { ...data },
        });
        idDiaAulas = data.id;
      }
      history.push(`/gerenciar-aula/${idDiaAulas}`);
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSalvando(false);
    }
  }, []);

  const handleSubmitAula = async ({
    aulas,
    outrasTurmas,
  }: {
    aulas: IAulasChamada[];
    outrasTurmas: boolean;
  }) => {
    try {
      setSalvando(true);

      const params = aulas.map((aula) => {
        return {
          id: aula.idDiaAulas || null,
          descricao: aula.descricaoChamada || null,
          anoLetivo: aula.anoLetivo,
          data: aula.data,
          aula: aula.aula,
          idMateria: aula.idMateria,
          idTurma: aula.idTurma,
          idSerie: aula.idSerie,
          idCurso: aula.idCurso,
          idProfessor:
            usuario?.tipoUsuario == EUserType.Professor
              ? usuario?.id || null
              : null,
          idUsuario:
            usuario?.tipoUsuario == EUserType.UsuarioSistema
              ? usuario?.id || null
              : null,
          diaSemAula: aula.diaSemAula || false,
          idMensagem: aula.idMensagem || null,
          naGrade: true,
          hora: aula.horaIni,
          duracao: aula.duracao,
          origem: EApplication.Educador,
        };
      });

      const { data } = await api.post<Partial<IDiaAula>[]>(
        `diaaula/lote/${outrasTurmas ? "1" : ""}`,
        params
      );

      for (let index = 0; index < data.length; index++) {
        const element = data[index];
        await gravaLogUser({
          logKey: {
            ...Log.Alunos.Incluir,
            acao: EAcaoLogUser.Inclusao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: element.idProfessor || undefined,
            idMateria: element.idMateria || undefined,
            idCurso: element.idCurso || undefined,
            idSerie: element.idSerie || undefined,
            idTurma: element.idTurma || undefined,
          },
          status: ELogUserStatus.Aguardando,
          message: `alterou ou incluiu um lote de aula: ${element.id}`,
          dataAfter: { ...element },
        });
      }
      carregar();
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSalvando(false);
    }
    hideDialogConteudo();
  };

  const showCheckbox =
    filter.idTurma != undefined && filter.idMateria != undefined;

  return (
    <Main
      title="Conteúdo ministrado"
      subtitle={`${turma?.tituloTurma ?? "Todas as turmas"} - ${
        materia?.nome ?? "Turmas as Matérias"
      }`}
      loading={loading}
      rightbarChildren={
        <Filter Component={Component} filter={filter} navigate={navigate} />
      }
      rightbarIcon={<FilterIcon />}
    >
      <Backdrop open={salvando} message="Gravando aulas..." />
      <DialogConteudo>
        {selecionados.length > 0 && (
          <EditAula
            grau={curso?.grau?.toString()}
            aulas={selecionados}
            close={hideDialogConteudo}
            submit={handleSubmitAula}
          />
        )}
      </DialogConteudo>
      {(!filter.idTurma ||
        !filter.etapa ||
        (!filter.idMateria &&
          usuario?.tipoUsuario != EUserType.UsuarioInterno)) &&
        !loading && (
          <EmptyState
            type="search"
            title="Selecione no filtro"
            subtitle="Selecione ao menos uma turma, matéria e uma etapa para apresentar o conteúdo ministrado"
          />
        )}
      {(diasAulasChamada ?? []).length > 0 && (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {showCheckbox && (
                    <TableCell
                      sx={({ spacing }) => ({ width: spacing(2) })}
                    ></TableCell>
                  )}
                  <TableCell
                    sx={({ spacing }) => ({ width: spacing(2) })}
                  ></TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: ({ spacing }) => spacing(8) }}
                  >
                    Data
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: ({ spacing }) => spacing(10) }}
                  >
                    Aula
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: ({ spacing }) => spacing(4) }}
                  >
                    Hora
                  </TableCell>
                  {!filter.idMateria && (
                    <TableCell sx={{ width: ({ spacing }) => spacing(16) }}>
                      Matéria
                    </TableCell>
                  )}
                  <TableCell>Conteúdo ministrado</TableCell>
                  <TableCell
                    sx={({ spacing }) => ({ width: spacing(2) })}
                  ></TableCell>
                  <TableCell
                    sx={({ spacing }) => ({ width: spacing(2) })}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(diasAulasChamada ?? [])
                  .filter(
                    (f) =>
                      filter.diaSemAula == undefined ||
                      (filter.diaSemAula && f.diaSemAula) ||
                      (!filter.diaSemAula && !f.diaSemAula)
                  )
                  .filter(
                    (f) =>
                      filter.tarefa == undefined ||
                      (filter.tarefa && f.tarefaChamada != undefined) ||
                      (!filter.tarefa && f.tarefaChamada == undefined)
                  )
                  .filter(
                    (f) =>
                      filter.somenteRecuperacao == undefined ||
                      (filter.somenteRecuperacao && f.isRecuperacao) ||
                      (!filter.somenteRecuperacao && !f.isRecuperacao)
                  )
                  .map((aula, index) => (
                    <TableRowAula
                      handleGerarAula={handleGerarAula}
                      selecionados={selecionados}
                      setSelecionados={setSelecionados}
                      key={`aula-${index}`}
                      aula={aula}
                      showCheckbox={showCheckbox}
                      materia={filter.idMateria ? true : false}
                      handleEdit={(m) => {
                        setSelecionados([m]);
                        showDialogConteudo();
                      }}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {filter.idTurma &&
        filter.etapa &&
        (diasAulasChamada ?? []).length == 0 &&
        !loading && (
          <EmptyState
            type="404"
            title="Nenhum conteúdo ministrado encontrado!"
            subtitle="O Filtro selecionado não encontrou nenhum conteúdo ministrado"
          />
        )}
      {selecionados.length > 1 && (
        <Float
          onClick={() => showDialogConteudo()}
          title="Adicionar ou Editar conteúdo"
          icon={<AddIcon />}
        />
      )}
    </Main>
  );
};
